// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useCallback } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { Stack, type TextareaProps } from '@mantine/core';

import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { TextareaWrapped } from 'components/Mantine/TextInputWrapped';

type Props = StrongOmit<TextareaProps, 'value' | 'defaultValue' | 'onChange' | 'onSubmit'> & {
  defaultValue: string;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
};

export const TextAreaWithButtons = ({ onSubmit, onClose, defaultValue, ...props }: Props) => {
  const [value, setValue] = useState(defaultValue);

  const [loading, setLoading] = useState(false);
  const handleSave = useCallback(async () => {
    setLoading(true);
    await onSubmit(value).finally(() => setLoading(false));
    onClose();
  }, [onSubmit, onClose, value]);

  return (
    <Stack>
      <TextareaWrapped {...props} value={value} onChange={setValue} disabled={loading} onSubmit={handleSave} />
      <ModalButtons loading={loading} hideDivider onConfirm={handleSave} onClose={onClose} />
    </Stack>
  );
};
